import { createRoute } from '@tanstack/react-router';
import { rootRoute } from './__root';

export const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <article
      style={{
        maxWidth: 720,
        margin: '0 auto',
        lineHeight: 1.65,
        fontSize: 15,
      }}
    >
      <h1 style={{ marginTop: 0 }}>Privacy Policy</h1>
      <p style={{ color: '#666' }}>Last updated: June 2026</p>

      <p>
        AI Assist is built on a "your data stays on your device" model. This page describes what
        we collect, what we don't, and where things live. Plain language; no dark patterns.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Account email and name</strong>, via Auth0, so you can sign in. Used to bind a
          Stripe customer record and your subscription state. Not sold, not shared.
        </li>
        <li>
          <strong>Stripe customer + subscription state</strong>, so we know whether to enable
          billing-gated features. Card numbers never touch our backend (Stripe Checkout is hosted
          by Stripe).
        </li>
        <li>
          <strong>Encrypted transcript blobs</strong>, only if you've explicitly turned on Cloud
          Sync. The transcripts are AES-256-GCM-encrypted on your device with a key derived from
          your local master key (HKDF-SHA256). We hold only ciphertext — we cannot decrypt your
          transcripts.
        </li>
      </ul>

      <h2>What we don't collect</h2>
      <ul>
        <li>
          <strong>Meeting audio.</strong> Audio is captured locally and sent to your own
          Whisper key. We never see it.
        </li>
        <li>
          <strong>API keys.</strong> Your Anthropic and OpenAI keys are stored locally on your
          machine in an encrypted file. They never leave your device.
        </li>
        <li>
          <strong>File contents.</strong> The directories you index with the helper stay on your
          machine. We don't see the files; we don't see the embeddings.
        </li>
        <li>
          <strong>Browsing history.</strong> The extension's content script attaches only to
          meeting URLs (Google Meet, Zoom Web, Teams Web). Nothing else.
        </li>
      </ul>

      <h2>Where data lives</h2>
      <ul>
        <li>
          <strong>On your machine</strong>: transcripts, indexed files, embeddings, API keys, the
          master key, pairing tokens.
        </li>
        <li>
          <strong>On our backend (Cloud Run + Cloud SQL, us-central1)</strong>: your account
          email, subscription state, Stripe customer ID, sync metadata (transcript IDs, ciphertext
          sizes, encrypted IVs — never plaintext).
        </li>
        <li>
          <strong>On GCS (encrypted blobs only)</strong>: ciphertext of your synced transcripts.
        </li>
        <li>
          <strong>On Stripe</strong>: card, subscription, billing history. Standard Stripe
          handling, governed by Stripe's own privacy policy.
        </li>
        <li>
          <strong>On Auth0</strong>: your sign-in identity (Google OAuth or email). Standard
          Auth0 handling.
        </li>
      </ul>

      <h2>Subprocessors</h2>
      <ul>
        <li>
          <strong>Google Cloud Platform</strong> — hosts our backend, database, and encrypted
          blob storage.
        </li>
        <li>
          <strong>Stripe</strong> — payments + subscription management.
        </li>
        <li>
          <strong>Auth0 (Okta)</strong> — sign-in.
        </li>
      </ul>
      <p>
        We don't use Whisper or Anthropic. You bring your own keys; your audio + LLM prompts go
        directly from your machine to your own provider account.
      </p>

      <h2>Your rights</h2>
      <ul>
        <li>
          <strong>Delete</strong>: go to Settings → Manage billing → "Cancel subscription" first,
          then email us at <a href="mailto:rfigueror@gmail.com">rfigueror@gmail.com</a> to delete
          your account. GDPR-compliant cascade delete (sync metadata, encrypted blobs, account
          row) within 30 days.
        </li>
        <li>
          <strong>Export</strong>: transcripts are also stored on your machine; you have them
          locally already. The Transcripts page in the web app lets you download as .txt or
          .md.
        </li>
        <li>
          <strong>Cancel</strong>: any time, via Stripe Customer Portal. Refunds within 30 days
          of purchase.
        </li>
      </ul>

      <h2>Contact</h2>
      <p>
        Questions, GDPR/CCPA requests, or anything we got wrong:{' '}
        <a href="mailto:rfigueror@gmail.com">rfigueror@gmail.com</a>.
      </p>

      <p style={{ color: '#666', marginTop: 32, fontSize: 13 }}>
        This policy applies to the AI Assist desktop helper, browser extension, and web app. We'll
        update this page if anything changes; check the "Last updated" date at the top.
      </p>
    </article>
  );
}
